import { useEffect, useState } from 'react';
import { useEmployee } from '../context/EmployeeContext';
import DefinitionList from '../../icib-admin/components/DefinitionList';
import LifeRail from '../../icib-admin/components/LifeRail';
import { fmtDate } from '../../icib-admin/utils/format';

// Same as the shared fmtDT but with the year — an employee may be looking
// back at an invitation from a previous year, so the year matters here.
const fmtDTYear = (s) =>
  s ? new Date(s).toLocaleString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/* EE-02 — View details of their schemes.
   "The system shall allow employees to view details of their schemes." —
   helps employees understand what cover is available to them
   (BrandonUserStoryAPI.md EE-02, GET /me/employees/{id}).

   EE-01's list (`db.employees`) is deliberately lean — one row per
   employer scheme, enrolments carrying just a bare `product_id`. This is
   the drill-down: pick a scheme, fetch its full record (`form_data` plus
   each enrolment resolved to its `product` — name + insurer_link) on
   demand via EmployeeContext#loadSchemeDetail, the same on-demand cache
   pattern icib-admin's EmployeesView uses for loadEmployeeDetail.
   `form_data` is view-only here — there's no employee-side PATCH for it
   (README §4.4); a correction has to go through ICIB admin. */
function SchemeView() {
  const { db, status, refreshing, schemeDetails, loadSchemeDetail } = useEmployee();
  const schemes = db.employees;
  const [selectedId, setSelectedId] = useState(null);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!selectedId && schemes.length) setSelectedId(schemes[0].id);
  }, [selectedId, schemes]);

  useEffect(() => {
    if (!selectedId) return;
    setDetailError(null);
    loadSchemeDetail(selectedId).then((res) => {
      if (res?.error) setDetailError(res.message || res.error);
    });
    // loadSchemeDetail isn't stable across renders (EmployeeContext
    // doesn't memoize it) — key off selectedId alone, same pattern as
    // icib-admin's EmployeeDrawer/TemplateDrawer.
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') {
    return <div className="card"><div className="empty"><b>Loading…</b></div></div>;
  }
  if (status === 'error' || !db.me) {
    return (
      <div className="card">
        <div className="empty">
          <b>Couldn't load your schemes</b>
          Check your connection and try again.
        </div>
      </div>
    );
  }

  const selected = schemes.find((e) => e.id === selectedId);
  const detail = selectedId ? schemeDetails[selectedId] : null;

  return (
    <>
      <div className="eyebrow">Benefit details</div>
      <div className="page-h"><h2>Benefit details</h2></div>
      <p className="page-sub">
        Full detail for a scheme you belong to — your submitted details and each benefit's enrolment status.{refreshing ? ' Refreshing…' : ''}
      </p>

      {db.errors?.employees && (
        <div className="note bad" style={{ marginBottom: 14 }}>
          <b>Couldn't load your schemes</b>{db.errors.employees}
        </div>
      )}

      {!schemes.length ? (
        <div className="card"><div className="empty"><b>No schemes yet</b>You're not currently enrolled in any employer scheme.</div></div>
      ) : (
        <>
          <div className="filterbar">
            {schemes.length > 1 ? (
              <select value={selectedId || ''} onChange={(e) => setSelectedId(e.target.value)}>
                {schemes.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.employer_client?.name || 'Employer scheme'}</option>
                ))}
              </select>
            ) : (
              <b>{selected?.employer_client?.name || 'Employer scheme'}</b>
            )}
            <span className="spacer" style={{ flex: 1 }} />
            <span className="ep">GET /me/employees/{'{id}'}</span>
          </div>

          <div className="card card-b">
            {detailError ? (
              <div className="empty"><b>Couldn't load this scheme</b>{detailError}</div>
            ) : !detail ? (
              <div className="empty"><b>Loading…</b></div>
            ) : (
              <>
                <div className="sec-h" style={{ marginTop: 0 }}><h3>Enrolments</h3></div>
                {(detail.enrolments || []).length ? detail.enrolments.map((en) => (
                  <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
                    <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                      <b>{en.product?.name || en.product_id}</b>
                      <span className="spacer" />
                      <LifeRail status={en.status} withLabel />
                    </div>
                    {en.product?.description && <p className="page-sub" style={{ marginTop: 0 }}>{en.product.description}</p>}
                    <DefinitionList rows={[
                      ['Insurer', en.product?.insurer_name || '—'],
                      ['Application Form', en.product?.insurer_link
                        ? <a href={en.product.insurer_link} target="_blank" rel="noreferrer">{en.product.insurer_link}</a>
                        : '—'],
                      ['Insurer scheme reference', en.product?.insurer_scheme_reference || '—'],
                      ['Eligibility', en.product?.eligibility_days != null ? `${en.product.eligibility_days} days` : '—'],
                      ['Information pack', en.product?.information_pack_link
                        ? <a href={en.product.information_pack_link} target="_blank" rel="noreferrer">{en.product.information_pack_link}</a>
                        : '—'],
                      ['Invited', fmtDTYear(en.invited_at)],
                      ['Employment effective date', en.effective_date ? fmtDate(en.effective_date) : '—'],
                      ['Removal effective date', en.removal_effective_date ? fmtDate(en.removal_effective_date) : '—'],
                    ]} />
                  </div>
                )) : <div className="empty"><b>No enrolments</b></div>}

                <div className="sec-h"><h3>Company-Specific extra details</h3></div>
                {Object.keys(detail.form_data || {}).length ? (
                  <DefinitionList rows={Object.entries(detail.form_data).map(([k, v]) => [k.replace(/_/g, ' '), String(v)])} />
                ) : (
                  <div className="empty"><b>No company-specific extra details</b></div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default SchemeView;
